import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import apiService from '../../services/api.service';
import { Team } from '../../types';

// Annulation d'inscription avec remboursement. Ce qui part chez l'athlète : la
// part organisateur par défaut, le total si l'organisateur prend les frais de
// service à sa charge (décoché par défaut : les CGU les laissent acquis).
interface Props {
  team: Team | null;
  onClose: () => void;
  onDone: () => void;
}

export function CancelRegistrationDialog({ team, onClose, onDone }: Props) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [refundFees, setRefundFees] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (team) { setReason(''); setRefundFees(false); setError(''); }
  }, [team]);

  const reg = team?.registration;
  const feeCents = reg?.amount_fee_cents ?? 0;
  const refundCents = refundFees
    ? (reg?.amount_total_cents ?? 0)
    : (reg?.amount_organizer_cents ?? reg?.amount_total_cents ?? 0);

  const confirm = async () => {
    if (!reg) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await apiService.cancelRegistration(reg.id, reason.trim(), refundFees);
      // L'athlète EST remboursé même quand la reprise chez l'organisateur
      // échoue : on le dit au lieu d'annoncer un succès complet.
      if (data?.warning) toast.warning(data.warning);
      else toast.success(t('teams.cancelRegistration.done', { amount: ((data?.refunded_cents || 0) / 100).toFixed(2) }));
      onDone();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!team} onOpenChange={(open) => { if (!open && !loading) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('teams.cancelRegistration.title')}
          </DialogTitle>
        </DialogHeader>

        {team && (
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
              <div>
                <div className="mb-1 text-xs text-muted-foreground">{t('teams.cancelRegistration.team')}</div>
                <div className="font-medium">#{team.team_id} — {team.name}</div>
              </div>
              <div className="text-right">
                <div className="mb-1 text-xs text-muted-foreground">{t('teams.cancelRegistration.refundAmount')}</div>
                <div className="text-lg font-bold">{(refundCents / 100).toFixed(2)}€</div>
              </div>
            </div>

            {feeCents > 0 && (
              <div className="flex items-start gap-3 rounded-lg border p-3">
                <Checkbox id="cancel-refund-fees" checked={refundFees} onCheckedChange={(v) => setRefundFees(v === true)} className="mt-0.5" />
                <div className="space-y-1">
                  <Label htmlFor="cancel-refund-fees" className="cursor-pointer">
                    {t('teams.cancelRegistration.refundFees', { amount: (feeCents / 100).toFixed(2) })}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {refundFees ? t('teams.cancelRegistration.refundFeesOn') : t('teams.cancelRegistration.refundFeesOff')}
                  </p>
                </div>
              </div>
            )}

            <Alert variant="destructive">
              <AlertTriangle size={16} />
              <AlertDescription>
                {refundFees ? t('teams.cancelRegistration.warningWithFees') : t('teams.cancelRegistration.warning')}
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="cancel-reason">{t('teams.cancelRegistration.reason')}</Label>
              <Input
                id="cancel-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('teams.cancelRegistration.reasonPlaceholder')}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground">{t('teams.cancelRegistration.reasonHelp')}</p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
          <Button variant="destructive" onClick={confirm} disabled={loading || reason.trim().length < 3}>
            {loading && <Loader2 size={16} className="mr-2 animate-spin" />}
            {t('teams.cancelRegistration.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
